import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { coy } from 'react-syntax-highlighter/dist/esm/styles/prism';
import '../Documentation.css';

function Documentation_v1() {
    return (
        <div className="doc-container">
            <header className="doc-header">
                <h1>Promo Language Documentation</h1>
            </header>

            <section className="doc-section">
                <h2>Table of Contents</h2>
                <ul>
                    <li><a href="#introduction">Introduction</a></li>
                    <li><a href="#ai-assisted-code-generation">AI-Assisted Code Generation in Promo</a></li>
                    <li><a href="#basic-syntax">Basic Syntax</a></li>
                    <li>
                        <a href="#data-types">Data Types</a>
                        <ul>
                            <li><a href="#numbers">2.a. Numbers</a></li>
                            <li><a href="#strings">2.b. Strings</a></li>
                            <li><a href="#variables">2.c. Variables</a></li>
                            <li><a href="#lists">2.d. Lists</a></li>
                            <li><a href="#structs">2.e. Structs</a></li>
                            <li><a href="#hashes">2.f. Hashes (Dictionaries)</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#control-structures">Control Structures</a>
                        <ul>
                            <li><a href="#if-expressions">3.a. if Expressions</a></li>
                            <li><a href="#while-loops">3.b. while Loops</a></li>
                            <li><a href="#for-loops">3.c. for Loops</a></li>
                            <li><a href="#let-bindings">3.d. let Bindings</a></li>
                            <li><a href="#begin-blocks">3.e. begin Blocks</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#functions">Functions</a>
                        <ul>
                            <li><a href="#defining-functions">4.a. Defining Functions</a></li>
                            <li><a href="#function-calls">4.b. Function Calls</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#special-forms">Special Forms</a>
                        <ul>
                            <li><a href="#define-and-set">5.a. define and set</a></li>
                            <li><a href="#ai-operator">5.b. ai Operator</a></li>
                        </ul>
                    </li>
                    <li>
                        <a href="#data-structure-operations">Data Structure Operations</a>
                        <ul>
                            <li><a href="#list-operations">6.a. List Operations</a></li>
                            <li><a href="#hash-operations">6.b. Hash Operations</a></li>
                            <li><a href="#struct-operations">6.c. Struct Operations</a></li>
                        </ul>
                    </li>
                    <li><a href="#interoperability-with-python">Interoperability with Python</a></li>
                    <li><a href="#examples">Examples</a></li>
                    <li><a href="#conclusion">Conclusion</a></li>
                </ul>
            </section>

            <section className="doc-section" id="introduction">
                <h2>Introduction</h2>
                <p>
                    Promo is a Lisp-like, dynamically-typed programming language designed for simplicity and extensibility.
                    It features a minimal syntax with powerful constructs for functional and procedural programming.
                    The language is implemented in Python and allows for interaction with AI models to generate code dynamically.
                </p>
            </section>

            <section className="doc-section" id="ai-assisted-code-generation">
                <h2>AI-Assisted Code Generation in Promo</h2>
                <p>
                    One of the standout features of Promo is its ability to generate and execute code dynamically using AI models.
                    The <code>ai</code> operator allows you to request the creation of functions on-the-fly based on provided arguments and context.
                </p>
                <p>The syntax for using the <code>ai</code> operator is as follows:</p>
                <SyntaxHighlighter language="lisp" >
                    {`(ai "function-name" arg1 arg2 ...)`}
                </SyntaxHighlighter>
                <p>
                    - <strong>"function-name"</strong>: A string representing the desired function name or operation.<br />
                    - <strong>arg1, arg2, ...</strong>: Arguments that provide context or data for the AI to generate the function.
                </p>
                <p>
                    When you invoke the <code>ai</code> operator, it interacts with an AI model to generate a function that performs the requested operation.
                    This function is then executed with the provided arguments.
                </p>
                <h3>Example Usage</h3>
                <p>
                    Suppose you want to add two numbers using AI-assisted code generation:
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(ai "add" 1 2)`}
                </SyntaxHighlighter>
                <p>
                    In this example, the AI model generates an addition function that adds 1 and 2, returning the result 3.
                </p>
                <h3>Benefits</h3>
                <ul>
                    <li>Enables dynamic code generation based on context.</li>
                    <li>Allows for high-level abstractions and rapid prototyping.</li>
                    <li>Leverages AI models to perform complex operations without explicit implementation.</li>
                </ul>
                <h3>Considerations</h3>
                <ul>
                    <li>Requires an active connection to the AI model backend.</li>
                    <li>Performance depends on the response time of the AI model.</li>
                    <li>Security considerations when executing generated code.</li>
                </ul>
            </section>

            <section className="doc-section" id="basic-syntax">
                <h2>Basic Syntax</h2>
                <p>
                    Expressions are written in prefix notation, and everything is an expression enclosed in parentheses.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define x 10)`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="data-types">
                <h2>Data Types</h2>
                <h3 id="numbers">2.a. Numbers</h3>
                <p>Integers and floating-point numbers are supported.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define a 5)`}
                </SyntaxHighlighter>

                <h3 id="strings">2.b. Strings</h3>
                <p>Strings are sequences of characters enclosed in double quotes.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define greeting "Hello, World!")`}
                </SyntaxHighlighter>

                <h3 id="variables">2.c. Variables</h3>
                <p>Variables are defined using <code>define</code> or <code>set</code>.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define x 42)
(set x 100)`}
                </SyntaxHighlighter>

                <h3 id="lists">2.d. Lists</h3>
                <p>Lists are fundamental data structures created using the <code>list</code> keyword or <code>cons</code>.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define my-list (list 1 2 3))
(define another-list (cons 0 my-list))`}
                </SyntaxHighlighter>

                <h3 id="structs">2.e. Structs</h3>
                <p>Custom data structures defined using <code>struct</code>.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(struct Point (x y))
(define p (Point 10 20))`}
                </SyntaxHighlighter>

                <h3 id="hashes">2.f. Hashes (Dictionaries)</h3>
                <p>Key-value pairs are stored in hashes.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define my-hash (hash "key1" "value1" "key2" "value2"))`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="control-structures">
                <h2>Control Structures</h2>
                <h3 id="if-expressions">3.a. if Expressions</h3>
                <p>Conditional execution based on a predicate.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(if (> x 0)
    (print "Positive")
    (print "Non-positive"))`}
                </SyntaxHighlighter>

                <h3 id="while-loops">3.b. while Loops</h3>
                <p>Execute a block of code repeatedly while a condition is true.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(while (< i 10)
    (print i)
    (set i (+ i 1)))`}
                </SyntaxHighlighter>

                <h3 id="for-loops">3.c. for Loops</h3>
                <p>Iterate over a list of values.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(for (item my-list)
    (print item))`}
                </SyntaxHighlighter>

                <h3 id="let-bindings">3.d. let Bindings</h3>
                <p>Create new variable bindings within a local scope.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(let ((x 10) (y 20))
    (print (+ x y)))`}
                </SyntaxHighlighter>

                <h3 id="begin-blocks">3.e. begin Blocks</h3>
                <p>Group multiple expressions to be evaluated sequentially.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(begin
    (define x 1)
    (set x (+ x 1))
    x)`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="functions">
                <h2>Functions</h2>
                <h3 id="defining-functions">4.a. Defining Functions</h3>
                <p>Functions are defined using <code>defn</code> or within <code>define</code>.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(define (square x)
    (* x x))`}
                </SyntaxHighlighter>

                <h3 id="function-calls">4.b. Function Calls</h3>
                <p>Call functions by placing the function name at the beginning of a list.</p>
                <SyntaxHighlighter language="lisp" >
                    {`(square 5) ; Returns 25`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="special-forms">
                <h2>Special Forms</h2>
                <h3 id="define-and-set">5.a. define and set</h3>
                <p>
                    <code>define</code> introduces a new variable or function, while <code>set</code> updates the value of an existing variable.
                </p>
                <SyntaxHighlighter language="lisp" >
                    {`(define pi 3.14)
(set pi 3.1415)`}
                </SyntaxHighlighter>

                <h3 id="ai-operator">5.b. ai Operator</h3>
                <p>
                    As previously discussed, the <code>ai</code> operator allows for dynamic function generation using AI models.
                </p>
                <p>Example usage:</p>
                <SyntaxHighlighter language="lisp" >
                    {`(ai "generate-summary" text)`}
                </SyntaxHighlighter>
                <p>
                    This will generate a <code>generate-summary</code> function that processes the provided <code>text</code>.
                </p>
            </section>

            <section className="doc-section" id="data-structure-operations">
                <h2>Data Structure Operations</h2>
                <h3 id="list-operations">6.a. List Operations</h3>
                <ul>
                    <li><code>cons</code>: Constructs a new list by adding an element to the front.</li>
                    <li><code>car</code>: Returns the first element of a list.</li>
                    <li><code>cdr</code>: Returns the rest of the list after the first element.</li>
                    <li><code>null</code>: Represents an empty list.</li>
                </ul>
                <SyntaxHighlighter language="lisp" >
                    {`(define lst (cons 1 (cons 2 null)))
(car lst) ; Returns 1
(cdr lst) ; Returns (2)`}
                </SyntaxHighlighter>

                <h3 id="hash-operations">6.b. Hash Operations</h3>
                <ul>
                    <li><code>hash</code>: Creates a new hash (dictionary).</li>
                    <li><code>get</code>: Retrieves a value from a hash by key.</li>
                </ul>
                <SyntaxHighlighter language="lisp" >
                    {`(define h (hash "name" "Alice" "age" 30))
(get h "name") ; Returns "Alice"`}
                </SyntaxHighlighter>

                <h3 id="struct-operations">6.c. Struct Operations</h3>
                <p><strong>Defining a Struct:</strong></p>
                <SyntaxHighlighter language="lisp" >
                    {`(struct Person (name age))`}
                </SyntaxHighlighter>
                <p><strong>Creating an Instance:</strong></p>
                <SyntaxHighlighter language="lisp" >
                    {`(define p (Person "Bob" 25))`}
                </SyntaxHighlighter>
                <p><strong>Accessing Fields:</strong></p>
                <SyntaxHighlighter language="lisp" >
                    {`(Person-name p) ; Returns "Bob"
(Person-age p) ; Returns 25`}
                </SyntaxHighlighter>
                <p><strong>Checking Instance Type:</strong></p>
                <SyntaxHighlighter language="lisp" >
                    {`(Person? p) ; Returns true`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="interoperability-with-python">
                <h2>Interoperability with Python</h2>
                <p>
                    Functions and variables can interact with Python code.
                    The <code>ai</code> operator can execute Python functions generated by AI models.
                    Python functions can be wrapped and called within Promo.
                </p>
            </section>

            <section className="doc-section" id="examples">
                <h2>Examples</h2>
                <h3>Defining and Using a Function</h3>
                <SyntaxHighlighter language="lisp" >
                    {`(define (add a b)
    (+ a b))

(add 5 7) ; Returns 12`}
                </SyntaxHighlighter>

                <h3>Using Control Structures</h3>
                <SyntaxHighlighter language="lisp" >
                    {`(define i 0)
(while (< i 5)
    (print i)
    (set i (+ i 1)))`}
                </SyntaxHighlighter>

                <h3>Working with Structs</h3>
                <SyntaxHighlighter language="lisp" >
                    {`(struct Rectangle (width height))
(define rect (Rectangle 10 20))
(Rectangle-width rect)  ; Returns 10
(Rectangle-height rect) ; Returns 20`}
                </SyntaxHighlighter>
            </section>

            <section className="doc-section" id="conclusion">
                <h2>Conclusion</h2>
                <p>
                    Promo represents an innovative fusion of Lisp-style code and natural language.
                    By abstracting the complexity of AI-based code generation, it empowers you to write clear, intention-revealing code without needing to know the underlying implementation details.
                </p>
                <p>
                    Use Promo to bridge the gap between human intent and machine execution.
                </p>
            </section>
        </div>
    );
}

export default Documentation_v1;
